/* The licensee console's shared state: the public world, read once from GET /v1/world; the account's licence and
   meters, read from GET /v1/usage on every page the reader opens and again whenever a run opens or closes (a hosted
   lane fills or frees), so a page that gates on the license never reads an old count; and a poll for what is still
   changing (a run being drawn, played or scored). */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAsync, type Async } from '../lib/hooks.tsx'
import { useLocation } from '../lib/router.tsx'
import { v1 } from './api.ts'
import { canOpen, licenceState, todayOf, type LicenceState, type Openable } from './model/licence.ts'
import { analysedRun } from './model/ward.ts'
import type { CaseClock } from './model/clock.ts'
import type { AnalysisBody, Membership, PublicWorld, Run, RunKind, Usage } from './types.ts'

const WorldCtx = createContext<Async<PublicWorld>>({ data: null, error: null, loading: true, reload: () => undefined })

export function PublicWorldProvider({ children }: { children: ReactNode }) {
  const world = useAsync((s) => v1.world(s), [])
  return <WorldCtx.Provider value={world}>{children}</WorldCtx.Provider>
}
export const usePublicWorld = () => useContext(WorldCtx)

/** The program invitations the account holds, and whether it is itself a program (the Program page is shown only to one). Read again on each page. */
const MemberCtx = createContext<Async<Membership>>({ data: null, error: null, loading: true, reload: () => undefined })
export const useMembership = () => useContext(MemberCtx)

/** Every run of the account, read again on each page: the command line numbers them (RUN 2) and the world shows the newest scored. */
const RunsCtx = createContext<Async<{ runs: Run[] }>>({ data: null, error: null, loading: true, reload: () => undefined })
export const useRuns = () => useContext(RunsCtx)

type LicenceApi = { usage: Async<Usage>; state: LicenceState | null; open: (kind: RunKind, opts?: { versionTested?: boolean }) => Openable }
const LicenceCtx = createContext<LicenceApi>({ usage: { data: null, error: null, loading: true, reload: () => undefined }, state: null, open: () => ({ ok: true }) })

/** The licence as the frame reads it. Inside the frame only: the sign-in page has no session to read it with. */
export function LicenceProvider({ children }: { children: ReactNode }) {
  const { path } = useLocation()
  // Read again on each page: what it reads is kept while the next read is in flight, so nothing flickers.
  const usage = useAsync((s) => v1.usage(s), [path])
  const world = useContext(WorldCtx).data
  const today = todayOf()
  const id = world ? { id: world.id, version: world.version } : null
  const state = usage.data ? licenceState(usage.data, today, id) : null
  const open = (kind: RunKind, opts: { versionTested?: boolean } = {}) => canOpen(kind, usage.data, today, id, opts)
  const membership = useAsync((s) => v1.membership(s), [path])
  const runs = useAsync((s) => v1.runs(s), [path])
  return (
    <LicenceCtx.Provider value={{ usage, state, open }}>
      <MemberCtx.Provider value={membership}>
        <RunsCtx.Provider value={runs}>{children}</RunsCtx.Provider>
      </MemberCtx.Provider>
    </LicenceCtx.Provider>
  )
}
export const useLicence = () => useContext(LicenceCtx)

/** Calls `reload` every few seconds while `live` is true. */
export function usePoll(live: boolean, reload: () => void, everyMs = 3000): void {
  const saved = useRef(reload)
  useEffect(() => {
    saved.current = reload
  }, [reload])
  useEffect(() => {
    if (!live) return
    const id = window.setInterval(() => saved.current(), everyMs)
    return () => window.clearInterval(id)
  }, [live, everyMs])
}

/** The case at the bedside: the Bedside page publishes the world's clock and the case's minutes while a case is open, and withdraws them when it is not. */
type CaseClockApi = { caseClock: CaseClock | null; publish: (c: CaseClock | null) => void; bedsideRun: string | null; publishRun: (id: string | null) => void }
const CaseClockCtx = createContext<CaseClockApi>({ caseClock: null, publish: () => undefined, bedsideRun: null, publishRun: () => undefined })
export function CaseClockProvider({ children }: { children: ReactNode }) {
  const [caseClock, setCaseClock] = useState<CaseClock | null>(null)
  const publish = useCallback((c: CaseClock | null) => setCaseClock((old) => (old === c || (old && c && old.clock === c.clock && old.t === c.t) ? old : c)), [])
  const [bedsideRun, publishRun] = useState<string | null>(null)
  const value = useMemo(() => ({ caseClock, publish, bedsideRun, publishRun }), [caseClock, publish, bedsideRun])
  return <CaseClockCtx.Provider value={value}>{children}</CaseClockCtx.Provider>
}
export const useCaseClock = () => useContext(CaseClockCtx)

/** The newest scored run and its run analysis, which marks the beds of the ward. `run` is null before any run is scored; `analysis` is read once the run is known. */
export function useLastAnalysis(): { run: Run | null; analysis: Async<AnalysisBody | null>; runs: Async<{ runs: Run[] }> } {
  const runs = useRuns()
  const run = useMemo(() => (runs.data ? analysedRun(runs.data.runs) : null), [runs.data])
  const analysis = useAsync((s) => (run ? v1.analysis(run.id, s) : Promise.resolve(null)), [run?.id])
  return { run, analysis, runs }
}
